import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import uniq from 'lodash/uniq';
import 'react-toastify/dist/ReactToastify.css';
import MenuShortcut from './MenuShortcut';

import useI18n from 'i18n';
import { useBridgeApi, useDispatch, useSelector } from 'Hooks';
import CategoryItem from './CategoryItem';
import Row from './Row';
import { checkOutOfStock } from '../../../utils/inventory';
import {
  getAllCategories,
  getAllCategoriesVariables,
  getAllCategories_allCategories_items as Item,
  getAllCategories_allCategories_nameLang,
} from './__queries__/getAllCategories';
import { getItems, getItems_allItems as SectionItem, getItemsVariables } from './__queries__';
import { useShoppingCart, useShoppingCartApi } from 'Components/ShoppingCartUniverse';
import PlaceholderItem from './PlaceholderItem';
import useEntryFinder from 'Hooks/useEntryFinder';
import { useStorage } from 'Components/Storage';
import { useConfig } from 'Components/ConfigProvider';
import { setLoginModal } from '../../../actions';
import { isItemHaveMSymbol } from '../../Item';
import useTakeAwayBag from '../../../Hooks/useTakeAwayBag';

const Padding = styled.div`
  height: 40px;
  background: #f8f8fb;
`;

export const GET_ITEMS = gql`
  query getItems($ids: [ID!]) {
    allItems(filter: { id_in: $ids, deleted: false }) {
      id
      order
      categories {
        id
      }
      disableMembershipDiscount
      title
      titleLang {
        id
        se
        en
        es
        no
      }
      descriptionLang {
        id
        se
        en
        es
        no
      }
      defaultPrice
      image {
        id
        file {
          id
          url
        }
      }
    }
  }
`;

export const CATEGORIES_QUERY = gql`
  query getAllCategories($companyId: ID!, $maxCategories: Int!, $maxItems: Int!) {
    allCategories(
      orderBy: order_DESC
      first: $maxCategories
      filter: {
        company: { id: $companyId }
        deleted: false
        OR: [{ hideInCustomerOrderingFlows: false }, { hideInCustomerOrderingFlows: null }]
      }
    ) {
      id
      order
      nameLang {
        id
        se
        en
        es
        no
      }
      items(orderBy: order_DESC, first: $maxItems, filter: { deleted: false }) {
        id
        order
        disableMembershipDiscount
        title
        titleLang {
          id
          se
          en
          es
          no
        }
        descriptionLang {
          id
          se
          en
          es
          no
        }
        defaultPrice
        image {
          id
          file {
            id
            url
          }
        }
      }
    }
  }
`;

type FirstCategories = {
  id: string;
  nameLang: getAllCategories_allCategories_nameLang | string;
  items: SectionItem[] | Item[];
};

/**
 * Home page categories component
 */
const Categories = () => {
  const config = useConfig();
  const dispatch = useDispatch();
  const maxCategories = config.MAX_CATEGORIES ?? 3;
  const landingPageSections = config.LANDING_PAGE_SECTIONS;
  const minRequestCategories = 7;
  const maxItems = 15;
  const params = useParams<{ venue_id?: string }>();
  const venueId = params?.venue_id || '';
  const { i18n } = useI18n();
  const { push } = useHistory();
  const api = useBridgeApi();
  const venue = useSelector((state) => state.venue);
  const qrOrder = useSelector((state) => state.qrOrder);
  const shoppingCartApi = useShoppingCartApi();
  const shoppingCart = useShoppingCart();
  const findEntryFromItem = useEntryFinder();
  const inventory = useSelector((state) => state.inventory ?? []);
  const [storageData] = useStorage();
  const authToken = storageData.authToken;
  const isLoggedIn = !!authToken;
  const categoriesLoadAttempt = useRef<number>(0);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const { loading: takeAwayBagLoading, addTakeAwayBag } = useTakeAwayBag();
  const menu = useSelector((state) => state.menu);

  const [getCategories, { data, loading }] = useLazyQuery<
    getAllCategories,
    getAllCategoriesVariables
  >(CATEGORIES_QUERY);
  const [getItemsData, { data: itemsData }] = useLazyQuery<getItems, getItemsVariables>(GET_ITEMS);

  const hasLandingPageSections = useMemo(() => {
    if (!landingPageSections || landingPageSections.length === 0 || !menu) {
      return false;
    }

    const filtered = landingPageSections.filter((s) => s.menuId === menu.menu.id);

    const itemIds = filtered.reduce((acc, currentValue) => {
      return uniq([...acc, ...currentValue.itemIds]);
    }, []);

    if (itemIds.length === 0) {
      return false;
    }

    return true;
  }, [landingPageSections, menu]);

  useEffect(() => {
    if (!menu) {
      return;
    }

    // Fetch items from home page sections if created in QuickOffice
    if (hasLandingPageSections) {
      fetchSectionsItems();
      return;
    }
    // Get first 7 categories
    fetchCategories(minRequestCategories);
  }, [hasLandingPageSections, menu]);

  // Fetch items from home page sections
  const fetchSectionsItems = useCallback(() => {
    if (!menu) {
      return;
    }

    const filteredSections = landingPageSections?.filter((s) => s.menuId === menu.menu.id);
    const ids = filteredSections?.reduce((acc, currentValue) => {
      return uniq([...acc, ...currentValue.itemIds]);
    }, []);

    if (ids.length === 0) {
      return;
    }

    getItemsData({
      variables: {
        ids: ids,
      },
    });
  }, [landingPageSections, menu]);

  // Refetch categories with updated quantity
  const fetchCategories = (quantity: number) => {
    categoriesLoadAttempt.current = categoriesLoadAttempt.current + 1;
    getCategories({
      variables: {
        companyId: config.COMPANY_ID,
        maxCategories: quantity,
        maxItems,
      },
    });
  };

  const firstCategories: FirstCategories[] = useMemo(() => {
    // Return items from home page sections if created in QuickOffice
    if (hasLandingPageSections) {
      if (!itemsData?.allItems) {
        return [];
      }

      const categories = landingPageSections
        ?.reduce((acc, section) => {
          const items: SectionItem[] = section.itemIds.reduce(
            (acc: SectionItem[], itemId: string) => {
              const item = itemsData?.allItems.find((a: SectionItem) => a.id === itemId);
              return item ? [...acc, item] : acc;
            },
            [],
          );

          const category = {
            id: items[0]?.categories[0]?.id ?? '', // get the first category id from the first item for link "View all"
            nameLang: section.title,
            items: items
              .filter((item) => findEntryFromItem(item.id))
              // Hide items that contain [m] in the title
              .filter((item) => !isItemHaveMSymbol(i18n(item.titleLang)))
              .slice(0, maxItems),
          };

          return [...acc, category];
        }, [])
        .filter(({ items }: { items: SectionItem[] }) => items.length > 0)
        .slice(0, maxCategories);

      setCategoriesLoading(false);
      return categories;
    }

    // Return items from categories if home page sections are not created in QuickOffice
    return (data?.allCategories || [])
      .map((category) => {
        return {
          id: category.id,
          nameLang: category.nameLang,
          items: category.items
            .filter((item) => findEntryFromItem(item.id))
            // Hide items that contain [m] in the title
            .filter((item) => !isItemHaveMSymbol(i18n(item.titleLang)))
            .slice(0, maxItems),
        };
      })
      .filter(({ items }) => items.length > 0)
      .slice(0, maxCategories);
  }, [data, itemsData, venue, findEntryFromItem, landingPageSections]);

  // Get more categories that have items in the menu until we have enough categories
  useEffect(() => {
    if (loading || !data || hasLandingPageSections) {
      return;
    }

    if (firstCategories.length < 1) {
      // The difference between the number of categories (have items) that we already have and that we need to get
      const count = maxCategories - firstCategories.length;
      const quantity = data.allCategories.length + count;
      if (categoriesLoadAttempt.current < 3) {
        fetchCategories(quantity);
      } else {
        setCategoriesLoading(false);
      }
    } else {
      setCategoriesLoading(false);
    }
  }, [data, loading, firstCategories, hasLandingPageSections]);

  const handleClick = (item: Item, categoryId: string) => {
    const entry = findEntryFromItem(item.id);

    // Joar: Don't think this would ever happen since we filter by entry above
    if (!entry) {
      return;
    }

    // If we are not adding to cart right away, redirect to item page
    if (!entry.instant) {
      push(`/order/${venueId}/item/${item.id}`);
      return;
      // Allow "Anonymous Purchase" check.
    } else if (!config.ANONYMOUS_PURCHASE && !isLoggedIn && (!qrOrder || !storageData.qrOrder)) {
      // If restaurant doesn't allow anonymous purchases & the user is not logged in, show login modal (Unless it's table ordering).
      dispatch(setLoginModal(true));
      return;
    }

    toast(i18n('Toast.Add', i18n(item.titleLang)), {
      position: 'bottom-center',
      autoClose: 1000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
    });
    api.vibrate('impactLight');
    if (entry && item) {
      shoppingCartApi.addItem(
        i18n(item.titleLang),
        entry.item.id,
        entry.id,
        [],
        [],
        entry.price,
        item.disableMembershipDiscount,
        categoryId,
      );
      // Add a takeaway bag if it is TAKE AWAY and there is a corresponding company
      addTakeAwayBag();
    }
  };

  const renderThumbnails = (items: SectionItem[] | Item[]) => {
    return items.some((item) => item.image);
  };

  if (!firstCategories.length || categoriesLoading || takeAwayBagLoading) {
    return (
      <>
        <Row title="Laddar...">
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
        </Row>
        <Row title="Laddar...">
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
        </Row>
        <Row title="Laddar...">
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
          <PlaceholderItem />
        </Row>
      </>
    );
  }

  const toMenu = (categoryId?: string) => {
    return categoryId
      ? push(`/order/${venueId}/menu/${categoryId}`)
      : push(`/order/${venueId}/menu`);
  };

  return (
    <>
      {firstCategories.map(({ id, nameLang, items }, index) => {
        // When checking stock for a category we disregard the count
        const categoryOutOfStock = checkOutOfStock(id, inventory, true);

        return (
          <Row
            key={id + index}
            title={i18n(nameLang)}
            categoryId={id}
            toMenu={toMenu}
            outOfStock={categoryOutOfStock}>
            {items.map((item, i) => {
              // This will never return null
              const entry = findEntryFromItem(item.id)!;

              return (
                <CategoryItem
                  key={item.id + i}
                  title={item.titleLang}
                  price={entry.price}
                  image={item?.image?.file?.url}
                  onClick={() => handleClick(item, id)}
                  outOfStock={{
                    item: checkOutOfStock(item.id, inventory, false, shoppingCart.items),
                    category: categoryOutOfStock,
                  }}
                  renderThumbnails={renderThumbnails(items)}
                />
              );
            })}
          </Row>
        );
      })}
      <MenuShortcut toMenu={toMenu} />
      <Padding />
    </>
  );
};

export default Categories;
